import clsx from "clsx";
import JSON5 from "json5";
import { FC, Fragment, ReactNode } from "react";

import { RecordTree } from "@tsmono/inspect-components/content";
import {
  MarkdownDivWithReferences,
  MarkdownReference,
} from "@tsmono/react/components";
import {
  formatPrettyDecimal,
  isRecord,
  printArray,
  printObject,
} from "@tsmono/util";

import {
  isArrayValue,
  isBooleanValue,
  isNullValue,
  isNumberValue,
  isObjectValue,
  isStringValue,
  ValueType,
} from "./types";
import styles from "./Value.module.css";

interface ValueInput {
  value: unknown;
  valueType: ValueType;
}

interface ValueProps {
  value: unknown;
  valueType: ValueType;
  identifier?: string;
  references?: MarkdownReference[];
  style?: "inline" | "block";
  maxTableSize?: number;
  interactive?: boolean;
  options?: {
    previewRefsOnHover?: boolean;
  };
}

export const Value: FC<ValueProps> = ({
  value,
  valueType,
  identifier,
  references,
  style = "block",
  maxTableSize = 5,
  interactive = false,
  options,
}): ReactNode => {
  const input: ValueInput = { value, valueType };

  if (isStringValue(input)) {
    return (
      <MarkdownDivWithReferences
        markdown={input.value}
        references={references}
        options={options}
      />
    );
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (isNumberValue(input) && input.value !== null) {
    return formatPrettyDecimal(input.value);
  } else if (isBooleanValue(input)) {
    return (
      <div
        className={clsx(
          styles.boolean,
          input.value ? styles.true : styles.false
        )}
      >
        {String(input.value)}
      </div>
    );
  } else if (isNullValue(input)) {
    return <code>null</code>;
  } else if (isArrayValue(input)) {
    return (
      <div title={JSON5.stringify(input.value, null, 2)}>
        <ValueList
          value={input.value}
          identifier={identifier}
          references={references}
          style={style}
          maxListSize={maxTableSize}
          interactive={interactive}
        />
      </div>
    );
  } else if (isObjectValue(input)) {
    return (
      <div
        title={
          maxTableSize < Object.keys(input.value).length
            ? JSON5.stringify(input.value, null, 2)
            : undefined
        }
      >
        <ValueTable
          value={input.value}
          identifier={identifier}
          references={references}
          style={style}
          maxTableSize={maxTableSize}
          interactive={interactive}
        />
      </div>
    );
  } else {
    return "Unknown value type";
  }
};

const ValueList: FC<{
  value: unknown[];
  identifier?: string;
  maxListSize: number;
  interactive: boolean;
  references?: MarkdownReference[];
  style: "inline" | "block";
}> = ({ value, identifier, maxListSize, interactive, references, style }) => {
  const itemsToDisplay = value.slice(0, maxListSize);

  return (
    <div
      className={clsx(styles.valueTable, style === "inline" && styles.inline)}
    >
      {itemsToDisplay.map((item, index) => {
        const displayValue = renderValue(
          index,
          item,
          identifier,
          references,
          interactive
        );
        return (
          <Fragment key={`value-table-row-${index}`}>
            <div
              className={clsx(
                styles.valueKey,
                "text-style-label",
                "text-style-secondary",
                "text-size-smallest"
              )}
            >
              [{index}]
            </div>
            <div className={clsx(styles.valueValue)}>{displayValue}</div>
          </Fragment>
        );
      })}
    </div>
  );
};

const ValueTable: FC<{
  value: Record<string, unknown>;
  identifier?: string;
  maxTableSize: number;
  interactive: boolean;
  references?: MarkdownReference[];
  style: "inline" | "block";
}> = ({ value, identifier, maxTableSize, interactive, references, style }) => {
  const sortedKeys = Object.keys(value).sort((a, b) => {
    const aVal = value[a];
    const bVal = value[b];
    if (typeof aVal === "boolean" && typeof bVal === "boolean") {
      return Number(bVal) - Number(aVal);
    } else if (typeof aVal === "number" && typeof bVal === "number") {
      return bVal - aVal;
    } else {
      return 0;
    }
  });

  const keysToDisplay = sortedKeys.slice(0, maxTableSize);
  const notShown = Object.keys(value).length - maxTableSize;

  return (
    <div
      className={clsx(styles.valueTable, style === "inline" && styles.inline)}
    >
      {keysToDisplay.map((key, index) => {
        const displayValue = renderValue(
          index,
          value[key],
          identifier,
          references,
          interactive
        );
        return (
          <Fragment key={`value-table-row-${key}`}>
            <div
              className={clsx(
                styles.valueKey,
                "text-style-label",
                "text-style-secondary",
                "text-size-smallest"
              )}
            >
              {key}
            </div>
            <div className={clsx(styles.valueValue)}>{displayValue}</div>
          </Fragment>
        );
      })}
      {notShown > 0 && (
        <Fragment key={`value-table-row-more`}>
          <div
            className={clsx(
              styles.valueKey,
              "text-style-label",
              "text-style-secondary",
              "text-size-smallest"
            )}
          >
            {notShown} more…
          </div>
          <div className={clsx(styles.valueValue)}></div>
        </Fragment>
      )}
    </div>
  );
};

const renderValue = (
  index: number,
  val: unknown,
  identifier: string | undefined,
  references: MarkdownReference[] | undefined,
  interactive: boolean
): ReactNode => {
  if (typeof val === "string") {
    return <MarkdownDivWithReferences markdown={val} references={references} />;
  } else if (typeof val === "number") {
    return formatPrettyDecimal(val);
  } else if (typeof val === "boolean") {
    return (
      <div className={clsx(styles.boolean, val ? styles.true : styles.false)}>
        {String(val)}
      </div>
    );
  } else if (val === null) {
    return <pre className={clsx(styles.value)}>null</pre>;
  } else if (Array.isArray(val)) {
    return printArray(val, 35);
  } else if (isRecord(val)) {
    return !interactive ? (
      printObject(val, 35)
    ) : (
      <RecordTree
        id={`value-record-${identifier ?? "na"}-${index}`}
        record={val}
      />
    );
  } else {
    return "Unknown value type";
  }
};
